/**
 * Runtime configuration management
 *
 * This module provides access to runtime configuration that can be injected
 * at container startup, allowing the same Docker image to work in multiple environments.
 *
 * The server injects runtime config directly into index.html at request time,
 * so no external script fetch is needed.
 */

import {
    RUNTIME_CONFIG_KEYS,
    RUNTIME_CONFIG_WINDOW_KEY,
    type RuntimeConfig,
    type RuntimeConfigKey,
} from '@shared/config';

// Extend window type to include runtime config
declare global {
    interface Window {
        [RUNTIME_CONFIG_WINDOW_KEY]?: RuntimeConfig;
    }
}

/**
 * Get a configuration value, preferring runtime config over build-time config
 *
 * @param key - The configuration key (without VITE_ prefix)
 * @param buildTimeValue - The build-time value from import.meta.env
 * @returns The configuration value
 */
export function getConfig<K extends RuntimeConfigKey>(key: K, buildTimeValue: string): string {
    // If runtime config exists and has the key, use it
    if (window[RUNTIME_CONFIG_WINDOW_KEY] && key in window[RUNTIME_CONFIG_WINDOW_KEY]) {
        return window[RUNTIME_CONFIG_WINDOW_KEY][key];
    }

    // Otherwise, fall back to build-time value
    return buildTimeValue;
}

/**
 * Get the full runtime configuration object
 * Falls back to build-time values if runtime config is not available
 */
export function getRuntimeConfig(): RuntimeConfig {
    if (window[RUNTIME_CONFIG_WINDOW_KEY]) {
        return window[RUNTIME_CONFIG_WINDOW_KEY];
    }

    // Fallback to build-time values
    const config = {} as RuntimeConfig;
    for (const key of RUNTIME_CONFIG_KEYS) {
        const envKey = `VITE_${key}` as keyof ImportMetaEnv;
        config[key] = (import.meta.env[envKey] as string) || '';
    }

    return config;
}

/**
 * Check if runtime configuration is available
 */
export function hasRuntimeConfig(): boolean {
    return typeof window !== 'undefined' && window[RUNTIME_CONFIG_WINDOW_KEY] !== undefined;
}

// Re-export types for convenience
export type { RuntimeConfig, RuntimeConfigKey };

// Log config status in development
if (import.meta.env.DEV) {
    console.log('[Config] Runtime config available:', hasRuntimeConfig());
}
